/**
 * Celine specific configuration.
 *
 * This will be merged with `src/config/index.ts`
 */

//import { isProd } from "../../utils/stage";
import customerDataForm from "./forms/customerData";
import customerConsentForm from "./forms/customerConsent";
import { fromDDCCustomer } from "../shared/transforms";
import { toDDCCustomerCeline } from "./transforms";
import debug from "../../utils/debug";

const stage: string = process.env.REACT_APP_STAGE || "local";
const AUTH_BASE_URL =
  "https://login.microsoftonline.com/e23236bc-386e-4844-8e94-cba011f218ae";
const [BACKEND_BASE_URL, AUTH_CLIENT_ID] =
  stage === "local" || stage === "dev" // localhost or dev
    ? [
        "https://celine.talend.uat.clienteling.lvmhda.com/services/rest/dcc/v1",
        "3ca3e730-8178-4d52-9ed7-c47f8cd078f9"
      ]
    : stage === "staging"
    ? [
        "https://celine.talend.uat.clienteling.lvmhda.com/services/rest/dcc/v1",
        "48d21d58-ce0d-4db9-8115-e71f1725dcfe"
      ]
    : [
        // prod
        "https://ddc-proxy.prd.celine.clienteling.lvmhda.com/services/rest/dcc/v1",
        "f70f903a-d3b4-416a-a49d-338d05a4e860"
      ];
debug("env:", process.env.NODE_ENV, process.env.REACT_APP_STAGE);
debug("baseUrl:", AUTH_BASE_URL, BACKEND_BASE_URL);

const GOOGLE_ANALYTICS_UA =
  stage === "prod" ? "UA-134358328-4" : "UA-134358328-3";

const supportedLanguages = [
  "ar",
  "cs",
  "de",
  "en",
  "es",
  "fr",
  "it",
  "ja",
  //"ko",
  "ms",
  "nl",
  "sv",
  "zh-Hans",
  "zh-Hant"
];

export default {
  id: "celine",
  brand: "Celine",
  brandContactAddress: "",
  brandContactEmail: "",
  services: {
    api: {
      baseUrl: BACKEND_BASE_URL,
      customerStatusEndpoint: "/clients?hashcode="
    },
    oidc: {
      baseUrl: AUTH_BASE_URL,
      loginRedirectEndpoint: "/oauth2/v2.0/authorize",
      scope: "openid offline_access",
      getTokenEndpoint: "/oauth2/v2.0/token",
      logoutRedirectEndpoint: "/oauth2/v2.0/logout",
      clientId: AUTH_CLIENT_ID,
      codeChallenge: "ThisIsntRandomButItNeedsToBe43CharactersLong",
      codeChallengeMethod: "plain",
      codeVerifier: "ThisIsntRandomButItNeedsToBe43CharactersLong",
      userInfoEndpoint: "https://graph.microsoft.com/oidc/userinfo"
    },
    googleMaps: {
      key: "AIzaSyDOOkauR7Y_q4Pls7XC8SPDsqQfHnAXBqo"
    },
    googleAnalytics: {
      ua: GOOGLE_ANALYTICS_UA
    }
  },
  errorCodes: {
    APP: "99_Internal app error",
    NETWORK: "01_Network connectivity issue",
    SERVER: "02_Server issue",
    DATA_CONNECTION: "03_Cegid server unresponsive",
    DATA_ERROR: "04_Cegid rejected data"
  },
  languages: {
    supportedLanguages,
    loadPath: "local"
  },
  notifications: {
    localStorageKey: "notificationsList"
  },
  zones: {
    europe: {
      languages: ["en", "fr", "es", "it", "de", "nl", "sv", "cs"],
      supportedLanguages
    },
    us: {
      languages: ["en", "es"],
      supportedLanguages: ["en", "es"]
    },
    ca: {
      languages: ["en", "fr"],
      supportedLanguages: ["en", "fr"]
    },
    cn: {
      languages: ["zh-Hans", "zh-Hant"],
      supportedLanguages: ["en", "zh-Hans", "zh-Hant"]
    },
    hk: {
      languages: ["zh-Hant", "zh-Hans"],
      supportedLanguages: ["en", "zh-Hant", "zh-Hans"]
    },
    mo: {
      languages: ["zh-Hant", "zh-Hans"],
      supportedLanguages: ["en", "zh-Hant", "zh-Hans"]
    },
    tw: {
      languages: ["zh-Hant", "zh-Hans"],
      supportedLanguages: ["en", "zh-Hant", "zh-Hans"]
    },
    th: {
      languages: ["en"],
      supportedLanguages: ["en"]
    },
    kr: {
      languages: ["ko"],
      supportedLanguages: ["en", "ko"]
    },
    krDepartmentStores: {
      languages: ["ko"],
      supportedLanguages: ["en", "ko"]
    },
    sg: {
      languages: ["en", "zh-Hant", "zh-Hans"],
      supportedLanguages: ["en", "zh-Hant", "zh-Hans"]
    },
    jp: {
      languages: ["ja"],
      supportedLanguages: ["en", "ja"]
    },
    jpDepartmentStores: {
      languages: ["ja"],
      supportedLanguages: ["en", "ja"]
    },
    au: {
      languages: ["en"],
      supportedLanguages: ["en"]
    },
    middleEast: {
      languages: ["en", "ar"],
      supportedLanguages: ["en", "ar"]
    },
    my: {
      languages: ["en", "ms"],
      supportedLanguages: ["en", "ms"]
    }
  },
  customise: {
    header: {
      showLanguage: false
    },
    footer: {
      showLanguage: true
    },
    languageSwitcher: {
      showOnlyZoneLanguages: true
    },
    notifications: {
      enableDisplayCustomer: false
    },
    fields: {
      markRequiredWithSymbol: "*",
      //customFields: ["honorificPrefixMy"],
      picklistValues: [
        {
          name: "honorificPrefixMy",
          options: [
            { value: "MLE", label: " MISS." },
            { value: "MR", label: " MR." },
            { value: "MME", label: " MRS." },
            { value: "MS", label: " MS." },
            { value: "DAO", label: "Dato'" },
            { value: "DAK", label: "Datuk" },
            { value: "DAN", label: "Datin" },
            { value: "DAI", label: "Dato' Sri" },
            { value: "DAE", label: "Datuk Seri" },
            { value: "DAS", label: "Datin Sri" },
            { value: "TAS", label: "Tan Sri" },
            { value: "PUS", label: "Puan Sri" },
            { value: "TEU", label: "Tengku" },
            { value: "TUK", label: "Tunku" },
            { value: "TUN", label: "Tun" },
            { value: "TOP", label: "Toh Puan" },
            { value: "PEN", label: "Pehin" }
          ]
        }
      ]
    },
    customer: {
      enableAdditionalInfo: false,
      enableRemoteCustomer: true,
      enableRemoteForZones: [
        "europe",
        "middleEast",
        "my",
        "us",
        "ca",
        "hk",
        "mo",
        "tw",
        "th",
        "kr",
        "krDepartmentStores",
        "sg",
        "jp",
        "jpDepartmentStores",
        "au"
      ]
    },
    verifySalesPerson: {
      resetOnActions: true
    }
  },
  transforms: {
    customer: {
      read: fromDDCCustomer,
      write: toDDCCustomerCeline
    }
  },
  forms: {
    schema: "form/customerFormDefinitions.json",
    fields: [
      "form/customerDataFields.json",
      "form/customerConsentFields.json",
      "form/customerThanks.json",
      "form/additionalInfoFields.json"
    ],
    customer: [customerDataForm, customerConsentForm]
  }
};
